import { Directive, ElementRef, OnDestroy, effect, inject, input } from '@angular/core';

/**
 * Shows an image as the element's background once the element comes into view, and `defaultImage`
 * until then, or for good when there is no image or it does not load (as ng-lazyload-image did on
 * the template cards). The image is loaded off the page first, so the background never shows half
 * an image.
 */
@Directive({selector: '[appLazyBackground]'})
export class LazyBackgroundDirective implements OnDestroy {
  private element = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly appLazyBackground = input<string | null | undefined>(null);
  readonly defaultImage = input('');

  private observer: IntersectionObserver | null = null;

  constructor() {
    effect(() => this.watch(this.appLazyBackground(), this.defaultImage()));
  }

  private watch(image: string | null | undefined, defaultImage: string): void {
    this.observer?.disconnect();
    this.show(defaultImage);
    if (!image) {
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }
      this.observer?.disconnect();
      const loader = new Image();
      loader.onload = () => {
        if (this.appLazyBackground() === image) {
          this.show(image);
        }
      };
      loader.src = image;
    });
    this.observer.observe(this.element.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private show(image: string): void {
    if (image) {
      this.element.nativeElement.style.backgroundImage = `url('${image}')`;
    }
  }
}
